import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivityService } from '@shared/services/activity.service';
import { HttpHelperService } from '@shared/services/http-helper.service';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { delay, exhaustMap, map } from 'rxjs/operators';
import { lastBuildTimestamp } from '../../../build-timestamp';

@Injectable({
  providedIn: 'root'
})
export class AppVersionService {
  triggerCheckNewVersion$ = new Subject();
  showAppUpdateSuggestion$ = new BehaviorSubject<{ force: boolean } | null>(null);
  private appBuildTS: number;
  private appStartTS = Date.now();
  private lastAppUpdateSuggestionDeclinedTSKey = 'lastAppUpdateSuggestionDeclinedTS';
  private lastAppUpdateSuggestionDeclinedTS: number;
  private updateSuggestionIntervalMS = 60 * 60 * 4 * 1000; // 4 hours

  constructor(
    private http: HttpClient,
    private activityService: ActivityService,
    private httpHelperService: HttpHelperService
  ) {
    this.appBuildTS = lastBuildTimestamp;
    this.lastAppUpdateSuggestionDeclinedTS = this.readLastDeclinedTS();

    this.triggerCheckNewVersion$.pipe(
      exhaustMap(() => this.checkIfNewVersionDeployed())
    ).subscribe({
      next: (data) => {
        if (data) {
          this.showUpdateSuggestionIfNeed();
        }
      }
    });

    this.activityService.getActivityStream(60 * 10).subscribe({
      next: (data) => {
        this.triggerCheckNewVersion$.next();
      }
    });
  }

  forceShowUpdateSuggestion() {
    this.showAppUpdateSuggestion$.next({ force: true });
  }

  declineUpdateSuggestion() {
    this.showAppUpdateSuggestion$.next(null);
    this.saveLastDeclineTS();
  }

  private readLastDeclinedTS() {
    const saved = localStorage.getItem(this.lastAppUpdateSuggestionDeclinedTSKey);
    if (saved && !isNaN(+JSON.parse(saved))) {
      return +JSON.parse(saved);
    } else {
      return 0;
    }
  }

  private saveLastDeclineTS() {
    this.lastAppUpdateSuggestionDeclinedTS = Date.now();
    localStorage.setItem(this.lastAppUpdateSuggestionDeclinedTSKey,
      JSON.stringify(this.lastAppUpdateSuggestionDeclinedTS))
  }

  private showUpdateSuggestionIfNeed() {
    if (Date.now() > this.lastAppUpdateSuggestionDeclinedTS + this.updateSuggestionIntervalMS) {
      // if app started less then 5 sec ago, wait a little
      const delayInterval = (this.appStartTS + 5000) - Date.now();
      of(Date.now()).pipe(
        delay(delayInterval > 0 ? delayInterval : 0)
      ).subscribe({
        next: (data) => {
          this.showAppUpdateSuggestion$.next({ force: false });
        }
      })
    }
  }

  private checkIfNewVersionDeployed(): Observable<boolean> {
    return this.getVersionFromServer().pipe(
      map(data => {
        return data !== this.appBuildTS;
      })
    );
  }

  private getVersionFromServer(): Observable<number> {
    return this.http.get(`assets/nocache/app-version.json`, {
      headers: this.httpHelperService.getHeadersWithDisabledLoader()
    }).pipe(
      map((data: any) => {
        if (data && data.appBuildTS) {
          const ts = +data.appBuildTS;
          if (!isNaN(ts)) {
            return ts;
          }
        }
        return 0;
      })
    );
  }
}
